import * as React from 'react';
import { Course } from 'sharedComponents/Overview/CourseCard';
import SideModal from 'sharedComponents/SideModal/SideModal';
import Tabs from 'sharedComponents/SideModal/Tabs';
import {
  TabContent,
  Body,
  Heading
} from 'sharedComponents/SideModal/Tabs/Tabs';
import { Background } from 'react-imgix';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import classNames from 'classnames';
import DetailField from './DetailField';
import Table, { TableRow } from 'sharedComponents/core/Table';
import Text from 'sharedComponents/core/Table/Text';
import Icon from 'sharedComponents/core/Icon';
import Fraction from './Fraction';
import SearchBar from './SearchBar';
import {
  ActiveDelegates,
  ExpiringSoonDelegates,
  HistoricalCourseTakers
} from './queries';
import { CourseStatus } from './__generated__/queries_ActiveDelegatesQuery.graphql';
import Paginator, { Page } from 'sharedComponents/Pagination/Paginator';
import { queries_HistoricalDelegatesQueryResponse } from './__generated__/queries_HistoricalDelegatesQuery.graphql';
import Spacer from 'sharedComponents/core/Spacers/Spacer';

const useStyles = createUseStyles((theme: Theme) => ({
  courseContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '25px 50px'
  },
  courseTitle: {
    fontSize: theme.fontSizes.heading,
    color: theme.colors.primaryWhite,
    fontWeight: '800',
    marginBottom: '10px'
  },
  aboutCourseContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  description: {
    width: '50%'
  },
  aboutCourseColumn: {
    display: 'flex',
    flexDirection: 'column'
  },
  searchBar: {
    marginBottom: '10px'
  },
  tableContainer: {
    backgroundColor: theme.colors.backgroundGrey,
    padding: '5px',
    overflowY: 'scroll',
    border: '1px solid #E9EBEB'
  },
  bodyScroll: {
    overflow: 'scroll'
  }
}));

type PageInfo = {
  currentPage: number;
  totalPages: number;
};

type Delegate = {
  firstName: string;
  lastName: string;
  email: string;
  active?: {
    status: CourseStatus;
    progress: {
      total: number;
      completed: number;
    };
  };
  expiring?: {
    certificateURL: string;
    expiryDate: string;
  };
};

const activityRow = (
  key: string | number,
  name: string, // firstName + lastName
  email: string,
  status: CourseStatus
): TableRow => ({
  key: key.toString(),
  cells: [
    {
      component: () => <Text text={name} />
    },
    {
      component: () => <Text text={email} />
    },
    {
      component: () => (
        <Icon
          name={
            status == 'complete'
              ? 'CourseStatus_Completed'
              : status == 'incomplete'
              ? 'CourseStatus_Incomplete'
              : 'CourseStatus_NotStarted'
          }
        />
      )
    }
  ]
});

const certificateRow = (
  key: string | number,
  name: string, // firstName + lastName
  email: string,
  certificateURL: string | undefined,
  expiryDate: string
): TableRow => ({
  key: key.toString(),
  cells: [
    {
      component: () => <Text text={name} />
    },
    {
      component: () => <Text text={email} />
    },
    {
      component: () => (
        <Icon name={'PDF_Icon'} onClick={() => window.open(certificateURL)} />
      )
    },
    {
      component: () => <Text text={expiryDate} formatDate />
    }
  ]
});

const infos: TabContent[] = [
  {
    key: 'Current Delegates',
    component: ({ state, setState }) => {
      const theme = useTheme();
      const classes = useStyles({ theme });

      const [debouncer, setDebouncer] = React.useState(400);
      const [searchText, setSearchText] = React.useState<string>();
      const [pageInfo, setPageInfo] = React.useState<PageInfo>({
        currentPage: 1,
        totalPages: 1
      });

      const onSearch = (name: string, offset: number) => {
        clearTimeout(debouncer);
        const timeout = setTimeout(async () => {
          setSearchText(name);
          if (name.length === 0 && state.activeDelegates.length > 0) {
            return;
          }

          // TODO: Implement search
        });
        setDebouncer(timeout);
      };

      return (
        <>
          <Body className={classes.bodyScroll}>
            <Heading>
              Currently{' '}
              <span style={{ color: '#1081AA' }}>
                {state.activeDelegates.length ?? 0} delegates
              </span>{' '}
              are valid in this course
            </Heading>
            <SearchBar
              onChange={(text) => onSearch(text, 0)}
              className={classes.searchBar}
            />
            <div className={classes.tableContainer}>
              <Table
                header={['NAME', 'EMAIL', 'STATUS']}
                rows={
                  state.activeDelegates
                    .filter((val: any) => {
                      if (searchText) {
                        return `${val.firstName} ${val.lastName}`
                          .toLowerCase()
                          .includes(searchText.toLowerCase());
                      }
                      return true;
                    })
                    .map((delegate: Delegate, i: number) =>
                      activityRow(
                        i,
                        `${delegate.firstName} ${delegate.lastName}`,
                        delegate.email,
                        'complete'
                      )
                    ) ?? []
                }
              />
            </div>
            <Spacer vertical spacing={2} />
            {/* <Paginator
              currentPage={pageInfo.currentPage}
              itemsPerPage={10}
              numPages={pageInfo?.totalPages}
              updatePage={({ offset }) =>
                onSearch(searchText ?? '', (offset - 1) * 10)
              }
              showRange={pageInfo.totalPages > 4 ? 4 : pageInfo.totalPages}
            /> */}
          </Body>
        </>
      );
    }
  },
  {
    key: 'Expiring Soon',
    component: ({ state, setState }) => {
      const theme = useTheme();
      const classes = useStyles({ theme });

      const [debouncer, setDebouncer] = React.useState(400);
      const [pageInfo, setPageInfo] = React.useState<PageInfo>({
        currentPage: 1,
        totalPages: 1
      });

      const onUpdatePage = ({ offset }: Page) => {
        clearTimeout(debouncer);
        const pageOffset = (offset - 1) * 10;
        const timeout = setTimeout(async () => {
          await state.expiringSoonOnUpdatePage(
            pageOffset,
            (page: PageInfo, delegates: Array<Delegate>) => {
              setPageInfo(page);
              setState((s: object) => ({
                ...s,
                expiringSoonDelegates: delegates
              }));
            }
          );
        });
        setDebouncer(timeout);
      };

      console.log('DEL', state.expiringSoonDelegates);
      return (
        <>
          <Body className={classes.bodyScroll}>
            <Heading>
              <span style={{ color: '#1081AA' }}>
                {state.expiringSoonDelegates.length ?? 0} certificates
              </span>{' '}
              will expire in the next 60 days
            </Heading>
            <div className={classes.tableContainer}>
              <Table
                header={['NAME', 'EMAIL', 'CERTIFICATE', 'EXPIRY DATE']}
                rows={
                  state.expiringSoonDelegates.map(
                    (delegate: Delegate, i: number) =>
                      certificateRow(
                        i,
                        `${delegate.firstName} ${delegate.lastName}`,
                        delegate.email,
                        delegate.expiring?.certificateURL,
                        delegate.expiring?.expiryDate ?? '03-05-2020'
                      )
                  ) ?? []
                }
              />
            </div>
            <Spacer vertical spacing={2} />
            <Paginator
              currentPage={pageInfo.currentPage}
              numPages={pageInfo.totalPages}
              updatePage={onUpdatePage}
              itemsPerPage={10}
              showRange={pageInfo.totalPages > 4 ? 4 : pageInfo.totalPages}
            />
          </Body>
        </>
      );
    }
  }
];

type Props = {
  course: Course;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  activeDelegates?: queries_HistoricalDelegatesQueryResponse;
  expiringSoonDelegates?: queries_HistoricalDelegatesQueryResponse;
};

const edgesToActiveDelegates = (
  res: queries_HistoricalDelegatesQueryResponse | undefined
): Array<Delegate> =>
  res?.historicalCourseTakers?.edges?.map((delegate: any) => ({
    firstName: delegate?.user.firstName ?? '',
    lastName: delegate?.user.lastName ?? '',
    email: delegate?.user.email ?? '',
    active: {
      progress: delegate?.progress ?? { completed: 0, total: 0 },
      status: delegate?.status ?? 'incomplete'
    }
  })) ?? [];

const edgesToExpiringSoonDelegates = (
  res: queries_HistoricalDelegatesQueryResponse | undefined
): Array<Delegate> =>
  res?.historicalCourseTakers?.edges?.map((delegate: any) => ({
    firstName: delegate?.user.firstName ?? '',
    lastName: delegate?.user.lastName ?? '',
    email: delegate?.user.email ?? '',
    expiring: {
      certificateURL: delegate?.certificateURL ?? '',
      expiryDate: delegate?.expiryDate ?? ''
    }
  })) ?? [];

function CourseInfo({
  course,
  isOpen,
  onClose,
  activeDelegates,
  expiringSoonDelegates
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const expiringSoonOnUpdatePage = (
    offset: number,
    onComplete: (pageInfo: PageInfo, delegates: Array<Delegate>) => void
  ) =>
    ExpiringSoonDelegates(course.id, 60, {
      offset: offset,
      limit: 10
    })
      .then((res) => {
        const pageProps = {
          total: res.historicalCourseTakers?.pageInfo?.total ?? 0,
          limit: res.historicalCourseTakers?.pageInfo?.limit ?? 10,
          offset: res.historicalCourseTakers?.pageInfo?.offset ?? 0
        };

        const pageInfo = {
          currentPage: Math.ceil(pageProps.offset / pageProps.limit),
          totalPages: Math.ceil(pageProps.total / pageProps.limit)
        };

        onComplete(pageInfo, edgesToExpiringSoonDelegates(res));
      })
      .catch((error) => console.error(error));

  return (
    <SideModal title="Course Details" closeModal={onClose} isOpen={isOpen}>
      <Background
        className={classNames(classes.courseContainer)}
        src={course.url}
        imgixParams={{ w: 300, h: 200, bri: -40 }}
      >
        <span className={classNames(classes.courseTitle)}>
          {`${course.title} ${course.type}`}
        </span>
        <div className={classNames(classes.aboutCourseContainer)}>
          <DetailField
            className={classes.description}
            heading="Description"
            detail={course.description}
          />
          <div className={classNames(classes.aboutCourseColumn)}>
            <DetailField
              heading="cost per delegate"
              detail={`£${course.price?.toFixed(2) ?? 0.0}`}
            />
            <DetailField
              heading="current seats"
              detail={`${
                activeDelegates?.historicalCourseTakers?.edges?.length ?? 0
              }`}
            />
          </div>
          <div className={classNames(classes.aboutCourseColumn)}>
            <DetailField
              heading="due to expire soon"
              detail={`${
                expiringSoonDelegates?.historicalCourseTakers?.edges?.length ??
                0
              }`}
            />
          </div>
        </div>
      </Background>

      <Tabs
        style={{ height: 'calc(100% - 197px)' }}
        content={infos}
        closeModal={onClose}
        initialState={{
          activeDelegates: edgesToActiveDelegates(activeDelegates),
          expiringSoonDelegates: edgesToExpiringSoonDelegates(
            expiringSoonDelegates
          ),
          expiringSoonOnUpdatePage: expiringSoonOnUpdatePage
        }}
      />
    </SideModal>
  );
}

export default CourseInfo;
